import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Api } from 'src/app/shared/constants/api';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { IPermissionWithShow } from 'src/app/shared/interface';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import * as _ from 'underscore';
import { IConsortium, IConsortiumCriteria } from '../consortium.interface';
import { Message } from 'src/app/shared/constants/message';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-consortium-list',
    templateUrl: './consortium-list.component.html',
    styleUrls: ['./consortium-list.component.less'],
    standalone: false
})
export class ConsortiumListComponent implements OnInit,AfterViewInit, OnDestroy {

  constructor(private formBuilder:FormBuilder,private service:DataService,
             private logger:NGXLogger,private utilityService:UtilityService,
             private permissionService:PermissionService, private activatedRoute:ActivatedRoute,
             private router:Router) { }

  consortiumListForm:FormGroup;
  consortiumDataSource=new MatTableDataSource<IConsortium>();
  statusTypes:any=[];
  totalRecords:any;
  pageSizeOptions=this.utilityService.getPageSizeOptions();
  consortiumColumns=['name','lineNames','destinationNames','vesselNames','status','action'];
  addPermission : boolean = false;
  editPermission : boolean = false;
  viewPermission : boolean = false;
  @ViewChild(MatSort)  sort:MatSort;
  @ViewChild(MatPaginator) paginator:MatPaginator;
  criteria:IConsortiumCriteria={};
	subscription = new Subscription();

  ngOnInit(): void {
    this.getAllPermission();
    this.initializeForm();
    this.getStatusTypes();
    this.getConsortiumList();
    this.setNameValueChangeEvent();
    this.setLineNameValueChangeEvent();
    this.setVesselNameValueChangeEvent();
    this.setStatusValueChangeEvent();
    this.setDestinationNameValueChangeEvent();
  }

  initializeForm(){
    this.consortiumListForm=this.formBuilder.group({
      name:[],
      lineName:[],
      destinationName:[],
      vesselName:[],
      status:[]
    })
  }

  ngAfterViewInit() {
    this.consortiumDataSource.paginator = this.paginator;
    this.consortiumDataSource.sort = this.sort;
    this.getConsortiumList();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  
  setNameValueChangeEvent(){
    this.subscription = this.name.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(nameValue => {
       this.criteria['name.contains']=nameValue?.toLowerCase();
       this.getConsortiumList(true);
    });
  }

  setDestinationNameValueChangeEvent(){
    const sub = this.destinationName.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(destinationName => {
       this.criteria.destinationName=destinationName?.toLowerCase();
       this.getConsortiumList(true);
    });
    this.subscription.add(sub);
  }

  setVesselNameValueChangeEvent(){
    const sub =this.vesselName.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(vesselName => {
       this.criteria.vesselName=vesselName?.toLowerCase();
       this.getConsortiumList(true);
    });
    this.subscription.add(sub);
  }

  setLineNameValueChangeEvent(){
    const sub = this.lineName.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(lineName => {
       this.criteria.lineName=lineName?.toLowerCase();
       this.getConsortiumList(true);
    });
    this.subscription.add(sub);
  }

  setStatusValueChangeEvent(){
    const sub = this.status.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe(statusValue => {
       this.criteria.status=statusValue?.toLowerCase();
       this.getConsortiumList(true);
    });
    this.subscription.add(sub);
  }

  openConsortiumForm(id?:number){
     if(id){
        this.editPermission ? this.router.navigate(['edit/'+id],{relativeTo:this.activatedRoute}) :
          this.utilityService.notifier(Message.EDIT_PERMISSION_WARNING,"warning");
      } else {
        this.addPermission ? this.router.navigate(['add'],{relativeTo:this.activatedRoute}) :
          this.utilityService.notifier(Message.ADD_PERMISSION_WARNING,"warning");
    }
  }

  getStatusTypes() {
    this.service.get(Api.STATUS_TYPE_LIST).subscribe(res => {
      this.statusTypes.push({key: "", label: "Search by Status"});
      this.statusTypes.push(...res);
    });
  }

  getConsortiumList(resetPageIndex: boolean =false){
    if (resetPageIndex) {
      this.paginator.pageIndex = 0;
    }
    if(this.viewPermission){
      this.criteria.page=this.paginator?.pageIndex;
      this.criteria.size=this.paginator?.pageSize;
      this.criteria.sort=this.criteria.sort?this.criteria.sort:['createdDate','desc'];
      this.service.get(Api.CONSORTIUM,this.criteria).subscribe(res => {
        if(res){
          if(res.length==0){
            this.utilityService.notifier('No records Found','info');
          }
          this.logger.info('Consortium List res',res);
          this.consortiumDataSource.data=res;
          this.totalRecords=res?.length;
        }
      });
    } else
        this.utilityService.notifier(Message.VIEW_PERMISSION_WARNING,"warning");
  }

  public getAllPermission(){
    const permissionCodes=[PermissionKey.CONSORTIUM_MODULE_ADD_PERMISSION,PermissionKey.CONSORTIUM_MODULE_VIEW_PERMISSION,PermissionKey.CONSORTIUM_MODULE_EDIT_PERMISSION];
    const permissionsWithShowValue:IPermissionWithShow[]=this.permissionService.checkPermissions(permissionCodes,PermissionKey.CONSORTIUM_MODULE);
    this.addPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.CONSORTIUM_MODULE_ADD_PERMISSION})?.show?true:false;
    this.viewPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.CONSORTIUM_MODULE_VIEW_PERMISSION})?.show?true:false;
    this.editPermission=_.findWhere(permissionsWithShowValue,{permissionCode:PermissionKey.CONSORTIUM_MODULE_EDIT_PERMISSION})?.show?true:false;
  }

  get name(){ return this.consortiumListForm.get('name'); }
  get lineName(){ return this.consortiumListForm.get('lineName'); }
  get destinationName(){ return this.consortiumListForm.get('destinationName'); }
  get vesselName(){ return this.consortiumListForm.get('vesselName'); }
  get status(){ return this.consortiumListForm.get('status'); }
}
